import { FC } from "react"

/**
 * Rendering helpers for pending-action payloads on the approval card. The
 * card is the human review gate for agent-proposed writes, so nested payload
 * values must be reviewable — never String(value) into "[object Object]".
 *
 * Update-style actions (e.g. update_connector) follow the payload convention
 * `{changes: {...}, previous: {...}}`; ChangesDiff renders those as a
 * before → after diff, showing only what actually differs (a 1-entry edit to
 * a 17-entry field mapping shows 1 removal + 1 addition, not 34 rows).
 */

type Json = unknown

export const isPlainObject = (v: Json): v is Record<string, Json> =>
  typeof v === "object" && v !== null && !Array.isArray(v)

/** Deterministic JSON (sorted object keys) so semantically equal values compare equal. */
export const stableStringify = (v: Json): string => {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`
  if (isPlainObject(v)) {
    return `{${Object.keys(v)
      .sort()
      .map(k => `${JSON.stringify(k)}:${stableStringify(v[k])}`)
      .join(",")}}`
  }
  return JSON.stringify(v) ?? "null"
}

const displayValue = (v: Json): string => {
  if (v === undefined || v === null || v === "") return "—"
  return typeof v === "string" ? v : JSON.stringify(v)
}

export interface ArrayDiff {
  removed: Json[]
  added: Json[]
  unchanged: number
}

/** Multiset diff of two arrays, order-insensitive per element. */
export const diffArrays = (prev: Json[], next: Json[]): ArrayDiff => {
  const counts = new Map<string, number>()
  for (const item of prev) {
    const key = stableStringify(item)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const added: Json[] = []
  let unchanged = 0
  for (const item of next) {
    const key = stableStringify(item)
    const remaining = counts.get(key) ?? 0
    if (remaining > 0) {
      counts.set(key, remaining - 1)
      unchanged += 1
    } else {
      added.push(item)
    }
  }
  const removed: Json[] = []
  for (const item of prev) {
    const key = stableStringify(item)
    const remaining = counts.get(key) ?? 0
    if (remaining > 0) {
      counts.set(key, remaining - 1)
      removed.push(item)
    }
  }
  return { removed, added, unchanged }
}

export interface ObjectDiffRow {
  key: string
  kind: "changed" | "added" | "removed"
  prev?: Json
  next?: Json
}

export const diffObjects = (
  prev: Record<string, Json>,
  next: Record<string, Json>
): { rows: ObjectDiffRow[]; unchanged: number } => {
  const rows: ObjectDiffRow[] = []
  let unchanged = 0
  const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]))
  for (const key of keys) {
    const inPrev = key in prev
    const inNext = key in next
    if (inPrev && inNext) {
      if (stableStringify(prev[key]) === stableStringify(next[key])) {
        unchanged += 1
      } else {
        rows.push({ key, kind: "changed", prev: prev[key], next: next[key] })
      }
    } else if (inNext) {
      rows.push({ key, kind: "added", next: next[key] })
    } else {
      rows.push({ key, kind: "removed", prev: prev[key] })
    }
  }
  return { rows, unchanged }
}

/** Generic payload value: scalars inline, nested values as collapsible JSON. */
export const PayloadValue: FC<{ value: Json }> = ({ value }) => {
  if (value === null || value === undefined) return <>—</>
  if (typeof value !== "object") return <>{String(value)}</>
  return (
    <details className="min-w-0">
      <summary className="cursor-pointer text-neutral-500 select-none">
        {Array.isArray(value)
          ? `${value.length} item${value.length === 1 ? "" : "s"}`
          : `${Object.keys(value).length} field${
              Object.keys(value).length === 1 ? "" : "s"
            }`}
      </summary>
      <pre className="mt-1 max-h-48 overflow-auto rounded bg-white/70 p-2 font-mono text-[11px] leading-relaxed text-neutral-700">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  )
}

const Mono: FC<{ children: string }> = ({ children }) => (
  <span className="break-all font-mono text-[11px]">{children}</span>
)

const NestedDiff: FC<{ field: string; prev: Json; next: Json }> = ({
  field,
  prev,
  next
}) => {
  if (Array.isArray(prev) && Array.isArray(next)) {
    const { removed, added, unchanged } = diffArrays(prev, next)
    return (
      <div>
        <p className="text-xs font-medium text-neutral-600">{field}</p>
        <ul className="mt-0.5 space-y-0.5">
          {removed.map((item, i) => (
            <li key={`r-${i}`} className="text-xs text-red-700">
              − <Mono>{JSON.stringify(item)}</Mono>
            </li>
          ))}
          {added.map((item, i) => (
            <li key={`a-${i}`} className="text-xs text-teal-700">
              + <Mono>{JSON.stringify(item)}</Mono>
            </li>
          ))}
          {unchanged > 0 && (
            <li className="text-[11px] text-neutral-500">
              {unchanged} unchanged {unchanged === 1 ? "entry" : "entries"}
            </li>
          )}
        </ul>
      </div>
    )
  }
  if (isPlainObject(prev) && isPlainObject(next)) {
    const { rows, unchanged } = diffObjects(prev, next)
    return (
      <div>
        <p className="text-xs font-medium text-neutral-600">{field}</p>
        <ul className="mt-0.5 space-y-0.5">
          {rows.map(row => (
            <li key={row.key} className="text-xs text-neutral-800">
              {row.kind === "removed" ? (
                <span className="text-red-700">
                  − {row.key}: <Mono>{displayValue(row.prev)}</Mono>
                </span>
              ) : row.kind === "added" ? (
                <span className="text-teal-700">
                  + {row.key}: <Mono>{displayValue(row.next)}</Mono>
                </span>
              ) : (
                <>
                  {row.key}: <Mono>{displayValue(row.prev)}</Mono>{" "}
                  <span aria-hidden="true">→</span>{" "}
                  <Mono>{displayValue(row.next)}</Mono>
                </>
              )}
            </li>
          ))}
          {unchanged > 0 && (
            <li className="text-[11px] text-neutral-500">
              {unchanged} unchanged {unchanged === 1 ? "entry" : "entries"}
            </li>
          )}
        </ul>
      </div>
    )
  }
  // Scalar or mixed types: plain before → after
  return (
    <p className="text-xs text-neutral-800">
      <span className="font-medium text-neutral-600">{field}</span>:{" "}
      <Mono>{displayValue(prev)}</Mono> <span aria-hidden="true">→</span>{" "}
      <Mono>{displayValue(next)}</Mono>
    </p>
  )
}

/**
 * Before → after view for update-style payloads carrying {changes, previous}.
 * `previous` is optional server-provided context; a field with no previous
 * value still renders (— → next).
 */
export const ChangesDiff: FC<{
  changes: Record<string, Json>
  previous: Record<string, Json> | null
}> = ({ changes, previous }) => (
  <div className="mt-2 space-y-2">
    {Object.entries(changes).map(([field, next]) => (
      <NestedDiff
        key={field}
        field={field}
        prev={previous ? previous[field] : undefined}
        next={next}
      />
    ))}
  </div>
)
